import { InputTextarea } from 'primereact/inputtextarea'
import { useMemo, useState } from 'react'

import { JsonViewer } from '../../../Components/Common/JsonViewer'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { CollapsibleSection } from './CollapsibleSection'
import { DebugToggle } from './DebugToggle'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Sezione avanzata della debug console: JSON editor custom, preview payload,
 * toggle audio message.
 * @param {Object} props
 * @param {Object} props.config - Stato corrente della configurazione
 * @param {Function} props.setField - Funzione per aggiornare un campo
 * @param {Function} props.buildPayload - Funzione per generare il payload corrente
 */
export function AdvancedSection({ config, setField, buildPayload }) {
  const [jsonError, setJsonError] = useState(null)

  const handleJsonChange = (value) => {
    setField('customJson', value)
    if (!value.trim()) {
      setJsonError(null)
      return
    }
    try {
      JSON.parse(value)
      setJsonError(null)
    } catch (e) {
      setJsonError(e.message)
    }
  }

  const previewPayload = useMemo(() => {
    if (!config.showPayloadPreview) return null
    try {
      return buildPayload('[preview message]')
    } catch (_) {
      return null
    }
  }, [config, buildPayload])

  return (
    <CollapsibleSection
      id="advanced"
      title={t.dbcAdvanced || 'Avanzato'}
      icon="pi pi-code"
      defaultCollapsed
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="debug-json-editor">
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink-500)',
              marginBottom: 6,
              display: 'block'
            }}
          >
            {t.dbcCustomJson || 'JSON Custom (sovrascrive campi payload)'}
          </label>
          <InputTextarea
            value={config.customJson}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={5}
            autoResize
            placeholder={'{\n  "key": "value"\n}'}
            style={{
              width: '100%',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              borderRadius: 'var(--radius-md)',
              borderColor: jsonError ? 'var(--danger)' : undefined
            }}
          />
          {jsonError && (
            <span style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4, display: 'block' }}>
              JSON non valido: {jsonError}
            </span>
          )}
        </div>

        <div className="debug-params-grid nora-test-params">
          <DebugToggle
            label={t.dbcAudioMessage || 'Audio Message'}
            checked={config.audioMessage}
            onChange={(value) => setField('audioMessage', value)}
          />

          <DebugToggle
            label={t.dbcShowPreview || 'Mostra Payload Preview'}
            checked={config.showPayloadPreview}
            onChange={(value) => setField('showPayloadPreview', value)}
            hint="Mostra il payload esatto che verrà inviato"
          />
        </div>

        {config.showPayloadPreview && previewPayload && (
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-500)',
                marginBottom: 6,
                display: 'block'
              }}
            >
              {t.dbcPayloadPreview || 'Preview Payload'}
            </label>
            <div className="debug-json-area">
              <JsonViewer src={previewPayload} name="payload" collapsed={1} />
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  )
}
